import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  CornerDownLeft,
  Loader2,
  Search,
  SearchX,
  Users,
} from "lucide-react";

import betaAPI from "v2/api";
import { dateLocale } from "i18n";
import { positionLabel } from "v2/constants/positions";
import {
  GROUPS,
  MIN_QUERY_LENGTH,
  flattenResults,
  highlightParts,
  moveIndex,
  parseDay,
} from "v2/utils/commandSearch";

const DEBOUNCE_MS = 180;

const Kbd = ({ children, className = "" }) => (
  <kbd
    className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-md border border-[#E5E7EB] bg-white px-1.5 font-sans text-[11px] font-medium text-[#6B7280] shadow-[0_1px_0_rgba(17,24,39,0.06)] ${className}`}
  >
    {children}
  </kbd>
);

const Highlight = ({ text, query }) =>
  highlightParts(text, query).map((part, i) =>
    part.match ? (
      <span key={i} className="text-[#E07A3A]">
        {part.text}
      </span>
    ) : (
      <React.Fragment key={i}>{part.text}</React.Fragment>
    )
  );

// Sport and category are stored as typed ("soccer"); show "Soccer" as the
// team list does.
const capitalizeWords = (text) =>
  text.replace(/\b\w/g, (letter) => letter.toUpperCase());

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const ResultIcon = ({ item }) => {
  if (item.group === "athletes") {
    return (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#E8854A] to-[#D66B2E] text-[12px] font-semibold text-white shadow-[0_2px_6px_rgba(214,107,46,0.35)]">
        {initials(item.title)}
      </span>
    );
  }
  if (item.group === "teams") {
    return (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FEF3E7] text-[#E07A3A]">
        <Users size={17} />
      </span>
    );
  }
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F3F4F6] text-[#4B5563]">
      <CalendarDays size={17} />
    </span>
  );
};

const SkeletonRows = () => (
  <div className="space-y-1 p-2" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2.5">
        <div className="h-9 w-9 animate-pulse rounded-xl bg-[#F3F4F6]" />
        <div className="flex-1 space-y-1.5">
          <div
            className="h-3 animate-pulse rounded bg-[#F3F4F6]"
            style={{ width: `${55 - i * 8}%` }}
          />
          <div className="h-2.5 w-1/4 animate-pulse rounded bg-[#F3F4F6]" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ icon: Icon, title, body, tone = "neutral" }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.18 }}
    className="flex flex-col items-center px-6 py-12 text-center"
  >
    <span
      className={`mb-3 flex h-12 w-12 items-center justify-center rounded-2xl ${
        tone === "brand"
          ? "bg-gradient-to-br from-[#FEF3E7] to-[#FDE6D3] text-[#E07A3A]"
          : "bg-[#F3F4F6] text-[#6B7280]"
      }`}
    >
      <Icon size={22} />
    </span>
    <p className="text-sm font-semibold text-[#111827]">{title}</p>
    <p className="mt-1 max-w-[320px] text-[13px] text-[#6B7280]">{body}</p>
  </motion.div>
);

/**
 * Workspace search in a glass dialog (SPO-1533). Opened from the navbar or
 * with Cmd/Ctrl+K. Finds teams, athletes and sessions by name.
 */
const CommandPalette = ({ open, onClose, workspaceId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [dataQuery, setDataQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const requestRef = useRef(0);

  const trimmed = query.trim();
  const searching = trimmed.length >= MIN_QUERY_LENGTH;
  const items = useMemo(
    () => (searching ? flattenResults(data, workspaceId) : []),
    [data, searching, workspaceId]
  );

  // Start fresh each time it opens, and for another workspace.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setData(null);
    setDataQuery("");
    setFailed(false);
    setActiveIndex(-1);
  }, [open, workspaceId]);

  // Keep the page behind still while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Search after a short pause in typing. Older responses are ignored.
  useEffect(() => {
    if (!open || !searching) {
      setLoading(false);
      return undefined;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await betaAPI.searchWorkspace({
          workspaceId,
          query: trimmed,
        });
        if (requestId !== requestRef.current) return;
        setData(res.data);
        setDataQuery(trimmed);
        setFailed(false);
        setActiveIndex(0);
      } catch (error) {
        if (requestId !== requestRef.current) return;
        console.error("Workspace search failed", error);
        setFailed(true);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, searching, trimmed, workspaceId]);

  // Keep the highlighted row in view.
  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const openItem = useCallback(
    (item) => {
      if (!item) return;
      onClose();
      navigate(item.to);
    },
    [navigate, onClose]
  );

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) =>
        moveIndex(i, e.key === "ArrowDown" ? 1 : -1, items.length)
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      openItem(items[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const subtitle = (item) => {
    if (item.group === "teams") {
      return [item.team.sport, item.team.category]
        .filter(Boolean)
        .map(capitalizeWords)
        .join(" · ");
    }
    if (item.group === "athletes") {
      const position = item.athlete.position
        ? positionLabel(item.athlete.position)
        : "";
      return [item.athlete.team_name, position].filter(Boolean).join(" · ");
    }
    const { session } = item;
    const day = parseDay(session.date);
    return [
      session.team_name,
      session.type ? t(`session.hero.types.${session.type}`) : "",
      day
        ? day.toLocaleDateString(dateLocale("en-GB"), {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const activeId =
    activeIndex >= 0 && items[activeIndex]
      ? `palette-option-${activeIndex}`
      : undefined;
  const showResults = searching && items.length > 0;
  const showSkeleton = searching && loading && !data;
  const showNoResults =
    searching &&
    !loading &&
    !failed &&
    data &&
    items.length === 0 &&
    dataQuery === trimmed;

  let body;
  if (!searching) {
    body = (
      <EmptyState
        icon={Search}
        tone="brand"
        title={t("navigation.search.idleTitle")}
        body={t("navigation.search.idleBody")}
      />
    );
  } else if (failed) {
    body = (
      <EmptyState
        icon={AlertCircle}
        title={t("navigation.search.failedTitle")}
        body={t("navigation.search.failedBody")}
      />
    );
  } else if (showSkeleton) {
    body = <SkeletonRows />;
  } else if (showNoResults) {
    body = (
      <EmptyState
        icon={SearchX}
        title={t("navigation.search.noResultsTitle", { query: trimmed })}
        body={t("navigation.search.noResultsBody")}
      />
    );
  } else if (showResults) {
    let index = -1;
    body = (
      <div role="listbox" id="palette-results" className="p-2">
        {GROUPS.map((group) => {
          const groupItems = items.filter((item) => item.group === group);
          if (!groupItems.length) return null;
          return (
            <div key={group} role="group" aria-labelledby={`palette-${group}`}>
              <p
                id={`palette-${group}`}
                className="px-3 pb-1.5 pt-3 text-xs font-medium text-[#9CA3AF] first:pt-1.5"
              >
                {t(`navigation.search.groups.${group}`)}
              </p>
              {groupItems.map((item) => {
                index += 1;
                const rowIndex = index;
                const active = rowIndex === activeIndex;
                return (
                  <div
                    key={item.key}
                    id={`palette-option-${rowIndex}`}
                    data-index={rowIndex}
                    role="option"
                    aria-selected={active}
                    onMouseMove={() => setActiveIndex(rowIndex)}
                    onClick={() => openItem(item)}
                    className="relative flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5"
                  >
                    {active && (
                      <motion.span
                        layoutId="palette-active-row"
                        className="absolute inset-0 rounded-xl bg-[#E07A3A]/[0.08] ring-1 ring-inset ring-[#E07A3A]/15"
                        transition={
                          reduceMotion
                            ? { duration: 0 }
                            : { type: "spring", stiffness: 520, damping: 40 }
                        }
                      />
                    )}
                    <span className="relative">
                      <ResultIcon item={item} />
                    </span>
                    <span className="relative min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-[#111827]">
                          {item.title ? (
                            <Highlight text={item.title} query={trimmed} />
                          ) : (
                            t("session.hero.untitled")
                          )}
                        </span>
                        {item.session?.status === "active" && (
                          <span className="flex shrink-0 items-center gap-1 rounded bg-[#DCFCE7] px-1.5 py-0.5 text-[10px] font-medium text-[#16A34A]">
                            <span className="relative flex h-1 w-1">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-75" />
                              <span className="relative inline-flex h-1 w-1 rounded-full bg-[#16A34A]" />
                            </span>
                            {t("navigation.search.live")}
                          </span>
                        )}
                      </span>
                      {subtitle(item) && (
                        <span className="block truncate text-xs text-[#6B7280]">
                          {subtitle(item)}
                        </span>
                      )}
                    </span>
                    <span
                      className={`relative flex items-center gap-1 text-[11px] font-medium text-[#E07A3A] transition-opacity duration-150 ${
                        active ? "opacity-100" : "opacity-0"
                      }`}
                      aria-hidden="true"
                    >
                      {t("navigation.search.open")}
                      <CornerDownLeft size={13} />
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  } else {
    body = <SkeletonRows />;
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="palette"
          className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[12vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
        >
          {/* Frosted backdrop */}
          <div
            className="absolute inset-0 bg-[#0F172A]/30 backdrop-blur-md backdrop-saturate-150"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t("navigation.search.dialogLabel")}
            className="relative w-full max-w-[640px] overflow-hidden rounded-[20px] border border-white/70 bg-white/[0.96] shadow-[0_24px_80px_-12px_rgba(15,23,42,0.35),0_0_0_1px_rgba(15,23,42,0.04)] backdrop-blur-2xl backdrop-saturate-150"
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -12, scale: 0.97 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }
            }
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
          >
            {/* Warm glow along the top edge */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-24"
              style={{
                background:
                  "radial-gradient(60% 100% at 50% 0%, rgba(232,133,74,0.14) 0%, rgba(232,133,74,0) 100%)",
              }}
              aria-hidden="true"
            />

            {/* Input */}
            <div className="relative flex items-center gap-3 border-b border-[#111827]/[0.06] px-5">
              {loading ? (
                <Loader2
                  size={20}
                  className="shrink-0 animate-spin text-[#E07A3A]"
                />
              ) : (
                <Search size={20} className="shrink-0 text-[#9CA3AF]" />
              )}
              <input
                ref={inputRef}
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("navigation.search.placeholder")}
                role="combobox"
                aria-expanded={showResults}
                aria-controls="palette-results"
                aria-activedescendant={activeId}
                aria-autocomplete="list"
                autoComplete="off"
                spellCheck={false}
                className="h-[60px] flex-1 text-base text-[#111827] placeholder-[#9CA3AF] outline-none"
                // The global input style sets a white background.
                style={{ backgroundColor: "transparent" }}
              />
              <button
                type="button"
                onClick={onClose}
                className="hidden sm:block"
                aria-label={t("navigation.search.close")}
              >
                <Kbd>{t("navigation.search.keys.esc")}</Kbd>
              </button>
            </div>

            {/* Results */}
            <div
              ref={listRef}
              className="relative max-h-[min(440px,55vh)] overflow-y-auto overscroll-contain"
            >
              {body}
            </div>

            {/* Footer */}
            <div className="relative hidden items-center gap-4 border-t border-[#111827]/[0.06] bg-[#F9FAFB]/70 px-5 py-2.5 text-[11px] text-[#6B7280] sm:flex">
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <ArrowUp size={11} />
                </Kbd>
                <Kbd>
                  <ArrowDown size={11} />
                </Kbd>
                {t("navigation.search.footer.move")}
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <CornerDownLeft size={11} />
                </Kbd>
                {t("navigation.search.footer.open")}
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>{t("navigation.search.keys.esc")}</Kbd>
                {t("navigation.search.footer.close")}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CommandPalette;
